import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client';

/**
 * Rank 4 — attack timeline + MTTD/MTTR/MTTA kill-chain metrics.
 *
 * Reads the score service's `getRangeTimeline` command, which correlates
 * phase-start telemetry events against `attack.detected` events for a range
 * (rank 1's telemetry table) and returns a merged, timestamped kill-chain
 * timeline plus computed metrics. Powers `AttackPhaseTimeline` + the metrics
 * card on the range page.
 *
 * Polls on the same cadence as the alert feed so detect-latency stays live as
 * detections land, without a WebSocket.
 */

export interface TimelinePhase {
  phaseId: string | null;
  startedAt: string | null;
  technique: string | null;
  detected: boolean;
  detectedAt: string | null;
  detectLatencySeconds: number | null;
  responseLatencySeconds: number | null;
  ackLatencySeconds: number | null;
  status: 'detected' | 'undetected';
}

export interface TimelineMetrics {
  mttdSeconds: number | null;
  mttrSeconds: number | null;
  mttaSeconds: number | null;
  phasesTotal: number;
  phasesDetected: number;
  detectionRate: number;
}

export interface RangeTimeline {
  rangeId: string;
  timeline: ReadonlyArray<TimelinePhase>;
  metrics: TimelineMetrics;
}

interface RawPhase {
  phase_id?: string | null;
  started_at?: string | null;
  technique?: string | null;
  detected?: boolean;
  detected_at?: string | null;
  detect_latency_seconds?: number | null;
  response_latency_seconds?: number | null;
  ack_latency_seconds?: number | null;
  status?: string;
}

interface RawTimelineResponse {
  rangeId?: string;
  timeline?: ReadonlyArray<RawPhase>;
  metrics?: {
    mttd_seconds?: number | null;
    mttr_seconds?: number | null;
    mtta_seconds?: number | null;
    phases_total?: number;
    phases_detected?: number;
    detection_rate?: number;
  };
}

const REFETCH_MS = 10_000;

function normalize(resp: RawTimelineResponse, rangeId: string): RangeTimeline {
  const m = resp.metrics ?? {};
  return {
    rangeId: resp.rangeId ?? rangeId,
    timeline: (resp.timeline ?? []).map((p) => ({
      phaseId: p.phase_id ?? null,
      startedAt: p.started_at ?? null,
      technique: p.technique ?? null,
      detected: Boolean(p.detected),
      detectedAt: p.detected_at ?? null,
      detectLatencySeconds: p.detect_latency_seconds ?? null,
      responseLatencySeconds: p.response_latency_seconds ?? null,
      ackLatencySeconds: p.ack_latency_seconds ?? null,
      status: p.status === 'detected' ? 'detected' : 'undetected',
    })),
    metrics: {
      mttdSeconds: m.mttd_seconds ?? null,
      mttrSeconds: m.mttr_seconds ?? null,
      mttaSeconds: m.mtta_seconds ?? null,
      phasesTotal: m.phases_total ?? 0,
      phasesDetected: m.phases_detected ?? 0,
      detectionRate: m.detection_rate ?? 0,
    },
  };
}

/** Correlated kill-chain timeline + MTTD/MTTR/MTTA for a single range. */
export function useRangeTimeline(rangeId: string | undefined) {
  return useQuery<RangeTimeline>({
    queryKey: ['timeline', 'range', rangeId],
    enabled: Boolean(rangeId),
    refetchInterval: REFETCH_MS,
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<RawTimelineResponse>(
        '/ranges/{rangeId}/timeline',
        { method: 'GET', pathParams: { rangeId: rangeId ?? '' }, signal },
      );
      return normalize(resp ?? {}, rangeId ?? '');
    },
  });
}
